/**
 * 通知中心（Header 铃铛下拉面板）
 * 数据来源：GET /api/v1/task（与 MyDances 同接口），仅展示进行中任务，30s 轮询刷新
 * 红点语义：存在未查看的进行中任务时点亮；打开面板后本会话内熄灭，出现新任务再点亮
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import {
  fetchTasks,
  formatRelativeTime,
  getTaskId,
  isTaskRunning,
  normalizeTaskStatus,
  TASK_STATUS_LABELS,
  type TaskItem,
} from '../utils/taskUtils';

const POLL_INTERVAL_MS = 30_000;
const NOTIFICATION_LIMIT = 20;

export default function NotificationCenter() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [runningTasks, setRunningTasks] = useState<TaskItem[]>([]);
  const [seenIds, setSeenIds] = useState<Set<string>>(() => new Set());

  const load = useCallback(async () => {
    try {
      const items = await fetchTasks({ limit: NOTIFICATION_LIMIT });
      setRunningTasks(items.filter(isTaskRunning));
    } catch (error) {
      // 轮询场景下静默失败，保留上次数据，避免高频 toast 刷屏
      console.error('Load notifications failed:', error);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const toggleOpen = () => {
    if (!open) {
      // 打开面板时把当前进行中任务标记为已看
      setSeenIds(new Set(runningTasks.map(getTaskId)));
    }
    setOpen(!open);
  };

  const close = () => setOpen(false);

  const goDances = () => {
    close();
    navigate('/my-dances');
  };

  const hasUnread = runningTasks.length > 0 && runningTasks.some((task) => !seenIds.has(getTaskId(task)));

  return (
    <div className="relative">
      <button
        onClick={toggleOpen}
        className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-black/[0.04] transition-colors relative"
        aria-label="通知中心"
      >
        <Bell size={18} className="text-text-secondary" />
        {hasUnread && <span className="absolute top-2 right-2 w-2 h-2 bg-danger rounded-full ring-2 ring-white animate-pulse-dot" />}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={close} />
          <div className="dropdown-enter absolute right-0 top-full mt-2.5 w-80 bg-white rounded-2xl shadow-dropdown p-5 z-40">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-text-primary">通知中心</p>
              {runningTasks.length > 0 && (
                <span className="text-[10px] text-text-muted bg-slate-100 px-2 py-0.5 rounded-full">{runningTasks.length} 条进行中</span>
              )}
            </div>

            {runningTasks.length > 0 ? (
              <div className="space-y-1">
                {runningTasks.map((task, index) => {
                  const status = normalizeTaskStatus(task.status ?? task.task_status);
                  const progress = task.progress;
                  return (
                    <button
                      key={`${getTaskId(task)}-${index}`}
                      onClick={goDances}
                      className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-warning-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                        <span className="w-2 h-2 bg-warning rounded-full animate-pulse-dot" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-text-primary font-medium truncate">{task.name ?? task.task_name ?? '未命名任务'}</p>
                        <p className="text-[10px] text-text-muted mt-0.5">
                          {TASK_STATUS_LABELS[status]}
                          {typeof progress === 'number' ? ` · 进度 ${progress}%` : ''}
                        </p>
                      </div>
                      <span className="text-[10px] text-text-muted shrink-0">{formatRelativeTime(task.created_at ?? task.created_timestamp ?? task.createdAt)}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-xs text-text-secondary">暂无进行中的任务</p>
              </div>
            )}

            <button onClick={goDances} className="w-full mt-3 py-2 text-xs text-brand font-medium hover:bg-brand-50 rounded-xl transition-colors">
              查看全部通知
            </button>
          </div>
        </>
      )}
    </div>
  );
}
